import {
  FC,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { css } from "@emotion/css";
import {
  InputLabel,
  Box,
  Autocomplete,
  ListItem,
  autocompleteClasses,
  OutlinedInput,
  Colors,
  TextField,
  Tooltip,
} from "@analyticsMD/quil";
import { AnalyticsBrowser } from "@segment/analytics-next";
import {
  FormControl,
  Chip,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import {
  createdPatientDispositions,
  updatePatientDispositions,
  deletePatientDispositions,
  putPatientDispositionsEvent,
} from "../api/endpoints";

import { useMutation, useQueryClient } from "react-query";
import { TypeChoices } from "../types/TypeChoices";
import { Sources } from "../types/Sources";
import type Disposition from "../types/Disposition";
import { ActionTrack } from "../types/Analytics";
import PatientDisposition from "../types/PatientDisposition";
import { useUpdatePatientCache } from "../hooks/use-update-patient-cache";
import { deepPurple } from "@mui/material/colors";
import {
  UI_ACCENT_BLUE,
  UI_ACCENT_BLUE_HOVER,
  UI_ACCENT_BLUE_OPTION_FOCUS,
} from "../constants/ui-colors";

const sxStyles = {
  selectWrapper: {
    display: "flex",
    width: "100%",
  },
  autoWrapper: {
    display: "content",
  },
  select: {
    borderRadius: "4px",
    fontSize: "0.75rem",
    fontWeight: "normal",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    [`& .${autocompleteClasses.popupIndicator}`]: {
      transform: "none",
      "&:hover": { background: "none" },
    },
  },
  input: {
    [`& .${autocompleteClasses.inputRoot}`]: {
      backgroundColor: "white",
      borderRadius: "0.625rem",
      flexWrap: "nowrap",
    },
    "& .MuiOutlinedInput-root": {
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: `${UI_ACCENT_BLUE} !important`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: `${UI_ACCENT_BLUE} !important`,
      },
    },
  },
  chip: {
    padding: "0.25rem",
  },
  /** Inline patient list: keep first chip + (+N) on one row; ellipsis long labels. */
  inlineMultiChipRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: "0.25rem",
    minWidth: 0,
    flex: "1 1 0%",
    maxWidth: "100%",
    overflow: "hidden",
  },
  inlinePrimaryChip: {
    minWidth: 0,
    maxWidth: "100%",
    flex: "1 1 auto",
    "& .MuiChip-label": {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  inlineCountChip: {
    flexShrink: 0,
  },
  inlineTooltip: {
    padding: 0,
    minWidth: "200px",
  },
  spinner: {
    mr: 1,
    color: deepPurple[600],
  },
};

const cssStyles = (isInline) => css`
  .MuiFormControl-root {
    display: inline;
    padding-bottom: 0;
    &:focus {
      background-color: transparent;
  }

  .MuiAutocomplete-inputRoot{
    gap: ${isInline ? "0" : "0.25rem"};
    padding: ${isInline ? "0.4rem 0.5rem" : "0.4rem 0.75rem 0"};
  }

  svg {
    font-size: 1.3rem;
    right: 0.9rem;
  }
  .MuiInputBase-input {
    text-align: ${isInline ? "left" : "center"};
    font-size: ${isInline ? ".9rem" : "0.75rem"};
  }

  .MuiAutocomplete-popupIndicator {
    right: ${isInline ? "1rem" : "0"};
    top: ${isInline ? "0.2rem" : "0"};
  }

  .MuiAutocomplete-popper{
    scrollbar-width: none;
    &::-webkit-scrollbar {
    background: transparent; /* Chrome/Safari/Webkit */
    width: 0;
  }
`;

const selectStyles = (isInline) =>
  isInline
    ? {
        ...sxStyles.select,
        minHeight: "2.5rem",
        height: "auto",
        width: "100%",
        boxShadow: "none",
        backgroundColor: Colors.white,
        textAlign: "left !important",
        border: `1px solid ${Colors.grey_15}`,
        fontSize: "1rem",
        "&:hover:not(.Mui-disabled)": {
          borderColor: `${UI_ACCENT_BLUE} !important`,
        },
        "&.Mui-focused": {
          borderColor: `${UI_ACCENT_BLUE} !important`,
        },
        "& .MuiOutlinedInput-root": {
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: `${UI_ACCENT_BLUE} !important`,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: `${UI_ACCENT_BLUE} !important`,
          },
        },
      }
    : {
        ...sxStyles.select,
        "& .MuiOutlinedInput-root": {
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: `${UI_ACCENT_BLUE} !important`,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: `${UI_ACCENT_BLUE} !important`,
          },
        },
      };

const AUTOCOMPLETE_LOADING_TEXT = "Saving...";

interface DispositionSelectorProps {
  currentDispositions: Disposition[];
  setCurrentDispositions: (
    dispositions: Disposition[] | ((prev: Disposition[]) => Disposition[]),
    isUserAction?: boolean
  ) => void;
  selectableDispositions: Disposition[];
  currentUserEnteredDispositions: PatientDisposition[];
  patientID: number;
  isMultiDispoEnabled: boolean;
  isInline?: boolean;
  analytics: AnalyticsBrowser;
  setShowErrorToast: Dispatch<SetStateAction<boolean>>;
  hasSuggestion?: boolean;
  disabled?: boolean;
  useSidebarView?: boolean;
}

const DispositionSelector: FC<DispositionSelectorProps> = ({
  currentDispositions,
  setCurrentDispositions,
  selectableDispositions,
  currentUserEnteredDispositions,
  patientID,
  isMultiDispoEnabled,
  isInline,
  analytics,
  setShowErrorToast,
  hasSuggestion = false,
  disabled,
  useSidebarView,
}) => {
  const [creatingPatientDispo, setCreatingPatientDispo] =
    useState<boolean>(false);
  const [validationError, setValidationError] = useState(false);
  const isMutatingRef = useRef<boolean>(false);

  const defaultDispo = useMemo<Disposition[] | Disposition>(() => {
    const defaultValue = {
      id: 0,
      display_name: "",
    } as Disposition;
    return isMultiDispoEnabled ? [defaultValue] : defaultValue;
  }, [isMultiDispoEnabled]);
  const [selectedDispositions, setSelectedDispositions] = useState<
    Disposition[] | Disposition
  >(defaultDispo);

  const queryClient = useQueryClient();
  const { upsertDisposition, removeDisposition } = useUpdatePatientCache();

  const addPatientDispositionsMutation = useMutation(
    createdPatientDispositions,
    {
      onSuccess: (data) => {
        // Update list cache with the new disposition
        if (!useSidebarView) {
          upsertDisposition(patientID, data);
        }
        queryClient.invalidateQueries({ queryKey: "patientdispositions" });

        // if we are in the patient list, we need to invalidate that as well
        if (isInline) {
          queryClient.invalidateQueries({
            queryKey: "patientdispositionslist",
          });
        }

        setCreatingPatientDispo(false);
      },
      onError: () => {
        // revert the change if the mutation fails for single dispo
        if (!isMultiDispoEnabled) {
          setSelectedDispositions(defaultDispo);
          setShowErrorToast(true);
        }
        setCreatingPatientDispo(false);
      },
    }
  );

  const updatePatientDispositionMutation = useMutation(
    updatePatientDispositions,
    {
      onSuccess: (data) => {
        // Update list cache with the updated disposition
        if (!useSidebarView) {
          upsertDisposition(patientID, data);
        }
        queryClient.invalidateQueries({ queryKey: "patientdispositions" });

        // if we are in the patient list, we need to invalidate that as well
        if (isInline) {
          queryClient.invalidateQueries({
            queryKey: "patientdispositionslist",
          });
        }
      },
      onError: (error, variables, context) => {
        // revert the change if the mutation fails for single dispo
        if (!isMultiDispoEnabled) {
          setCurrentDispositions(userEnteredDispositions, false);
          setShowErrorToast(true);
        }
      },
    }
  );

  const deletePatientDispositionMutation = useMutation(
    deletePatientDispositions,
    {
      onSuccess: (data, variables) => {
        // Update list cache - remove the deleted disposition
        if (!useSidebarView) {
          removeDisposition(patientID, variables);
        }
        // Keep pending deletions until API confirms...
      },
      onError: (error, variables, context) => {
        // revert the change if the mutation fails
        setCurrentDispositions(userEnteredDispositions, false);
        setShowErrorToast(true);
      },
    }
  );

  const putPatientDispositionsEventMutation = useMutation(
    putPatientDispositionsEvent
  );

  const isAnyMutationPending =
    addPatientDispositionsMutation.isLoading ||
    updatePatientDispositionMutation.isLoading ||
    deletePatientDispositionMutation.isLoading ||
    creatingPatientDispo;

  useEffect(() => {
    isMutatingRef.current = isAnyMutationPending;
  }, [isAnyMutationPending]);

  const userEnteredDispositions = useMemo(() => {
    return (
      currentUserEnteredDispositions
        .map((pd) => pd.disposition)
        .sort((a, b) => a.display_name.localeCompare(b.display_name)) || []
    );
  }, [currentUserEnteredDispositions]);

  // Update selected disposition based on parent state (currentDispositions)
  // This is the primary source for selectedDispositions - user actions update currentDispositions first
  useEffect(() => {
    // Deduplicate
    const deduplicated = isMultiDispoEnabled
      ? (currentDispositions as Disposition[]).filter(
          (d, index, self) =>
            index === self.findIndex((item) => item.id === d.id)
        )
      : currentDispositions;

    // Only update if deduplicated has values (don't overwrite with empty unless explicitly empty)
    if (isMultiDispoEnabled) {
      if (Array.isArray(deduplicated) && deduplicated.length > 0) {
        setSelectedDispositions(deduplicated);
      } else if (currentDispositions.length === 0) {
        // Explicitly empty in multi mode - set to empty array
        setSelectedDispositions([]);
      }
    } else {
      if (deduplicated.length > 0) {
        setSelectedDispositions(deduplicated[0]);
      }
      // For single mode with empty, keep defaultDispo (don't override)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDispositions, isMultiDispoEnabled]);

  const applyChangeForSingleDisposition = (newDisposition: Disposition) => {
    if (disabled) {
      return;
    }
    // Check both props and local state to avoid using stale data
    // If local state is empty (we just deleted everything), always create
    const hasExistingDispo =
      currentUserEnteredDispositions.length > 0 &&
      currentDispositions.length > 0;

    if (hasExistingDispo) {
      const user_entered = currentUserEnteredDispositions[0];
      updatePatientDispositionMutation.mutate({
        id: user_entered.id,
        disposition_type: TypeChoices.USER_ENTERED,
        disposition: newDisposition.id,
        source: Sources.USER_CREATED,
        // clear intelligence fields
        suggested_patient_disposition_id: null,
        decision_updated_ts: null,
      });
    } else {
      setCreatingPatientDispo(true);
      addPatientDispositionsMutation.mutate({
        disposition_type: TypeChoices.USER_ENTERED,
        disposition: newDisposition.id,
        source: Sources.USER_CREATED,
        patient: patientID,
      });
    }

    // optimistically set the dispo
    setSelectedDispositions(newDisposition);

    analytics?.track(ActionTrack.DISPOSITION_INTERACTION, {
      disposition_value: newDisposition.display_name,
      disposition_id: newDisposition.id,
    });
  };

  const applyDispositionsChanges = async (
    dispositionsOverride?: Disposition[]
  ) => {
    if (disabled) {
      return;
    }
    // change in single disposition mode is applied in handleChange method
    if (!isMultiDispoEnabled) {
      return;
    }

    // Only consider dispositions that exist in both props AND local state as "existing"
    // This prevents trying to update dispositions that were just deleted
    const localDispoIds = new Set(
      (currentDispositions as Disposition[]).map((d) => d.id)
    );
    const validCurrentUserEntered = currentUserEnteredDispositions.filter(
      (pd) => localDispoIds.has(pd.disposition.id)
    );

    // Figure out adds and deletions
    const oldDispositionsIds = validCurrentUserEntered.map(
      (patientDispo) => patientDispo.disposition.id
    );
    // Use dispositionsOverride if provided (for immediate updates), otherwise use currentDispositions
    // This allows us to pass the updated value directly when state hasn't updated yet
    const dispositionsToUse =
      dispositionsOverride ?? (currentDispositions as Disposition[]);
    const newDispositionsIds = dispositionsToUse.map((dispo) => dispo.id);
    const setOldDispositionsIds = new Set(oldDispositionsIds);
    const setNewDispositionsIds = new Set(newDispositionsIds);
    const dispositionsToAdd = newDispositionsIds.filter(
      (dispo) => !setOldDispositionsIds.has(dispo)
    );
    const dispositionsToDelete = oldDispositionsIds.filter(
      (dispo) => !setNewDispositionsIds.has(dispo)
    );

    // We only want to mutate patientDispositions where the disposition is not included in the selection already
    const patientDispositionsToMutate = validCurrentUserEntered.filter(
      (patientDispo) =>
        !newDispositionsIds.includes(patientDispo.disposition.id)
    );

    const patientDispoUpdated = [];
    const mutationsRequests = [];

    for (const dispositionId of dispositionsToAdd) {
      const currentPatientDispo = patientDispositionsToMutate.pop();
      // if we have a user entered dispo, just update
      if (currentPatientDispo) {
        mutationsRequests.push(
          updatePatientDispositionMutation.mutateAsync({
            id: currentPatientDispo.id,
            disposition_type: TypeChoices.USER_ENTERED,
            disposition: dispositionId,
            source: Sources.USER_CREATED,
            // clear intelligence fields
            suggested_patient_disposition_id: null,
            decision_updated_ts: null,
          })
        );
        patientDispoUpdated.push(currentPatientDispo.id);
      } else {
        mutationsRequests.push(
          addPatientDispositionsMutation.mutateAsync({
            disposition: dispositionId,
            disposition_type: TypeChoices.USER_ENTERED,
            source: Sources.USER_CREATED,
            patient: patientID,
          })
        );
      }
    }
    const patientDispositionsToDelete = currentUserEnteredDispositions.filter(
      (patientDispo) =>
        dispositionsToDelete.includes(patientDispo.disposition.id) &&
        // If patientDispo was updated there is no need to delete it
        !patientDispoUpdated.includes(patientDispo.id)
    );

    for (const patientDispo of patientDispositionsToDelete) {
      mutationsRequests.push(
        deletePatientDispositionMutation.mutateAsync(patientDispo.id)
      );
    }

    Promise.allSettled(mutationsRequests).then((results) => {
      const anyRejected = results.some(
        (result) => result.status === "rejected"
      );
      if (anyRejected) {
        setShowErrorToast(true);
        // Deduplicate when reverting to prevent duplicates
        const deduplicated = isMultiDispoEnabled
          ? userEnteredDispositions.filter(
              (dispo, index, self) =>
                index === self.findIndex((d) => d.id === dispo.id)
            )
          : userEnteredDispositions;
        setCurrentDispositions(
          isMultiDispoEnabled
            ? deduplicated
            : deduplicated.length > 0
            ? [deduplicated[0]]
            : [],
          false
        );
      }

      // Only invalidate queries if there were no deletions
      // For deletions, delay invalidation to allow backend to process
      const hasDeletions = patientDispositionsToDelete.length > 0;

      if (hasDeletions) {
        setTimeout(() => {
          queryClient.invalidateQueries("patientdispositions");
        }, 500); // 500ms delay should be enough for backend to process
      } else {
        // For add/update operations, invalidate immediately (like selection behavior)
        queryClient.invalidateQueries("patientdispositions");
      }

      // Update autocomplete with the disposition we actually changed
      putPatientDispositionsEventMutation.mutate(patientID);
    });

    analytics?.track(ActionTrack.DISPOSITION_INTERACTION, {
      disposition_values: (currentDispositions as Disposition[]).map(
        (dispo) => dispo.display_name
      ),
      disposition_ids: (currentDispositions as Disposition[]).map(
        (dispo) => dispo.id
      ),
    });
  };

  const handleChange = (dispositions: Disposition[] | Disposition) => {
    if (disabled) {
      return;
    }
    // Check if we are in multi disposition mode
    if (isMultiDispoEnabled && Array.isArray(dispositions)) {
      // Allow empty selection - handle it the same way as chip delete
      if (dispositions.length === 0) {
        setCurrentDispositions([]);
        setSelectedDispositions([]); // Update immediately for consistency
        setValidationError(true);
        // Apply changes immediately when selection becomes empty
        // Pass empty array directly to avoid reading stale state (state updates are async)
        // This ensures the deletion is applied even if the view closes immediately
        applyDispositionsChanges([]);
        return;
      }
      // Deduplicate to prevent duplicates
      const deduplicated = dispositions.filter(
        (dispo, index, self) =>
          index === self.findIndex((d) => d.id === dispo.id)
      );
      setValidationError(false);
      setCurrentDispositions(deduplicated);
      setSelectedDispositions(deduplicated); // update immediately for consistency
    } else {
      const newDisposition = dispositions as Disposition;
      setCurrentDispositions([newDisposition]);
      applyChangeForSingleDisposition(newDisposition);
    }
  };

  const handleChipDelete = (tagToDelete: Disposition) => {
    if (disabled || !isMultiDispoEnabled) return;

    // Read from currentDispositions (source of truth) instead of selectedDispositions
    // to avoid timing issues with useEffect synchronization
    const currentTags = Array.isArray(currentDispositions)
      ? currentDispositions
      : [];

    // Filter out the deleted tag and deduplicate
    const updatedTags = currentTags
      .filter((tag) => tag.id !== tagToDelete.id)
      .filter(
        (tag, index, self) => index === self.findIndex((t) => t.id === tag.id)
      );

    // Optimistically update UI immediately
    setCurrentDispositions(updatedTags);
    setSelectedDispositions(updatedTags);

    // Apply changes to backend
    if (updatedTags.length > 0) {
      applyDispositionsChanges(updatedTags);
    } else {
      // Empty selection - apply immediately
      applyDispositionsChanges([]);
      setValidationError(true);
    }
  };

  const autoCompleteOptions = creatingPatientDispo
    ? []
    : selectableDispositions.sort((a, b) =>
        a.display_name.localeCompare(b.display_name)
      );

  const inputComponent = useCallback(
    (params) => {
      const isEmpty =
        currentDispositions.length === 0 &&
        userEnteredDispositions.length === 0;

      if (isInline) {
        return (
          <TextField
            {...params}
            placeholder={isEmpty ? "Select a disposition" : ""}
            error={
              !!validationError && isMultiDispoEnabled && !isAnyMutationPending
            }
            helperText={
              validationError && isMultiDispoEnabled && !isAnyMutationPending
                ? "Missing disposition"
                : ""
            }
            sx={{
              [`& .${autocompleteClasses.inputRoot}`]: {
                flexWrap: "nowrap",
                border: "none",
                justifyContent: "left",
                minHeight: isMultiDispoEnabled ? "2.5rem" : "auto",
                alignItems: "center",
                minWidth: 0,
                maxWidth: "100%",
              },
              [`& .${autocompleteClasses.input}`]: {
                minWidth:
                  isMultiDispoEnabled && currentDispositions.length > 0
                    ? "0.9rem !important"
                    : "10rem !important",
                flexGrow:
                  isMultiDispoEnabled && currentDispositions.length > 0
                    ? "0"
                    : "1",
              },
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${UI_ACCENT_BLUE} !important`,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${UI_ACCENT_BLUE} !important`,
                },
              },
              padding: "0 !important",
            }}
          />
        );
      }

      return (
        <FormControl
          variant="outlined"
          sx={{ width: "100%" }}
          error={
            !!validationError && isMultiDispoEnabled && !isAnyMutationPending
          }
        >
          <InputLabel
            sx={{
              top: "9px",
              left: "12px",
              fontSize: "0.8rem",
            }}
            htmlFor="dispo-input"
          >
            Dispo
          </InputLabel>
          <OutlinedInput
            {...params.InputProps}
            id="dispo-input"
            style={{ fontSize: "0.9rem", width: "100%" }}
            sx={{
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: `${UI_ACCENT_BLUE} !important`,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${UI_ACCENT_BLUE} !important`,
              },
            }}
            label="Dispo"
            notched
            error={
              !!validationError && isMultiDispoEnabled && !isAnyMutationPending
            }
            inputProps={{
              ...params.inputProps,
              placeholder: isEmpty ? "Select a disposition" : "",
              style: {
                textAlign: "left",
                paddingLeft: "0.5rem",
                fontSize: "0.9rem",
                minWidth:
                  isMultiDispoEnabled && currentDispositions.length > 0
                    ? "0"
                    : undefined,
                width:
                  isMultiDispoEnabled && currentDispositions.length > 0
                    ? "0"
                    : undefined,
                flex:
                  isMultiDispoEnabled && currentDispositions.length > 0
                    ? "0 0 0"
                    : undefined,
              },
            }}
          />
          {validationError && isMultiDispoEnabled && !isAnyMutationPending && (
            <FormHelperText>Missing disposition</FormHelperText>
          )}
        </FormControl>
      );
    },
    [
      isInline,
      isMultiDispoEnabled,
      isAnyMutationPending,
      currentDispositions.length,
      userEnteredDispositions.length,
      validationError,
    ]
  );

  const popperOffset = hasSuggestion ? (isInline ? 47 : 42) : 0;

  return (
    <Box sx={sxStyles.selectWrapper}>
      {selectableDispositions.length > 0 ? (
        <Autocomplete
          loading={creatingPatientDispo || isAnyMutationPending}
          loadingText={AUTOCOMPLETE_LOADING_TEXT}
          popupIcon={
            // add a loading spinner when updates are happening
            isAnyMutationPending ? (
              <CircularProgress
                size={16}
                sx={
                  isInline
                    ? sxStyles.spinner
                    : { ...sxStyles.spinner, padding: "0.3rem" }
                }
              />
            ) : undefined // undefined uses the default arrow
          }
          fullWidth
          openOnFocus
          autoHighlight
          blurOnSelect={!isMultiDispoEnabled}
          multiple={isMultiDispoEnabled}
          disableCloseOnSelect={isMultiDispoEnabled}
          disableClearable
          disabled={disabled || isAnyMutationPending}
          sx={selectStyles(isInline)}
          renderOption={(props, option: Disposition, { selected }) => (
            <ListItem
              {...props}
              key={`disposition-option-${option.id}`}
              sx={{
                fontSize: isInline ? "0.9rem" : "0.75rem",
                "&:hover": {
                  backgroundColor: `${UI_ACCENT_BLUE_HOVER} !important`,
                },
                "&.Mui-focused": {
                  backgroundColor: `${UI_ACCENT_BLUE_OPTION_FOCUS} !important`,
                },
                '&[aria-selected="true"]': {
                  backgroundColor: `${UI_ACCENT_BLUE_OPTION_FOCUS} !important`,
                },
              }}
            >
              {option.display_name}
            </ListItem>
          )}
          renderInput={inputComponent}
          options={autoCompleteOptions}
          isOptionEqualToValue={(option: Disposition, value: Disposition) => {
            return option.display_name === value.display_name;
          }}
          getOptionLabel={(option: Disposition) => option.display_name}
          className={cssStyles(isInline)}
          componentsProps={{
            paper: {
              sx: {
                borderRadius: isInline ? "0.25rem" : "0.625rem",
                maxHeight: 300,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              },
            },
          }}
          slotProps={{
            popper: {
              placement: "bottom-start",
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, popperOffset], // Add extra offset when suggestion is present
                  },
                },
              ],
              sx: {
                zIndex: hasSuggestion ? 1200 : 1300, // Lower z-index when suggestion is present
              },
            },
          }}
          onChange={(event: any, newValue: Disposition[] | Disposition) => {
            handleChange(newValue);
          }}
          onClose={() => {
            if (!disabled) {
              applyDispositionsChanges();
            }
          }}
          value={selectedDispositions}
          // Used for multiple selection
          renderTags={(tags) => {
            // Ensure tags is an array (renderTags is only used in multi mode)
            const tagsArray: Disposition[] = Array.isArray(tags)
              ? (tags as Disposition[])
              : [];

            // In inline mode, show only first chip + count chip if there are multiple
            if (isInline && tagsArray.length > 1) {
              const firstTag = tagsArray[0];
              const remainingTags = tagsArray.slice(1);
              return (
                <Box sx={sxStyles.inlineMultiChipRow}>
                  <Chip
                    key={`disposition-chip-${firstTag.id}-0`}
                    label={firstTag.display_name}
                    size="small"
                    sx={{
                      ...sxStyles.chip,
                      ...sxStyles.inlinePrimaryChip,
                    }}
                    onDelete={
                      disabled || isAnyMutationPending
                        ? undefined
                        : () => handleChipDelete(firstTag)
                    }
                  />
                  <Tooltip
                    title={
                      <Box
                        sx={{
                          padding: "0.5rem 0.75rem 0.5rem 0.5rem",
                          "& ul": {
                            margin: 0,
                            paddingLeft: "1rem",
                            paddingRight: 0,
                          },
                          "& li": {
                            marginBottom: "0.25rem",
                            "&:last-child": {
                              marginBottom: 0,
                            },
                          },
                        }}
                      >
                        <ul>
                          {remainingTags.map((tag: Disposition, index) => (
                            <li key={`dispo-tooltip-${tag.id}-${index}`}>
                              {tag.display_name}
                            </li>
                          ))}
                        </ul>
                      </Box>
                    }
                    placement="top"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: sxStyles.inlineTooltip,
                      },
                    }}
                  >
                    <Chip
                      key="disposition-chip-count"
                      label={`(+${tagsArray.length - 1})`}
                      size="small"
                      sx={{
                        ...sxStyles.chip,
                        ...sxStyles.inlineCountChip,
                        cursor: "default",
                        "& .MuiChip-deleteIcon": {
                          display: "none",
                        },
                      }}
                    />
                  </Tooltip>
                </Box>
              );
            }
            // Show all chips in non-inline mode or when there's only one chip
            return tagsArray.map((tag: Disposition, index) => (
              <Chip
                key={`disposition-chip-${tag.id}-${index}`}
                label={tag.display_name}
                size="small"
                sx={sxStyles.chip}
                onDelete={
                  disabled || isAnyMutationPending
                    ? undefined
                    : () => handleChipDelete(tag)
                }
              />
            ));
          }}
        />
      ) : null}
    </Box>
  );
};

export default DispositionSelector;
